import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { waLink } from '../../lib/wa.js';
import { SearchableSelect } from '../primitives/SearchableSelect.js';
import { WaIcon } from '../primitives/WaIcon.js';
import { DEPLOY_DURATIONS } from '@cls/shared';
import { useReferenceList } from '../../features/reference/useReferenceList.js';

interface Contact { id: string; name: string; role: string | null; phone: string | null; customer: string | null; }

interface ProjectMeta {
  id: string;
  code: string;
  name: string;
  address: string | null;
  postcode: string | null;
  supervisorId: string | null;
  clientPmId: string | null;
  clsManagerId: string | null;
}

interface VacancyMeta {
  id: string;
  startDate: string;
  duration: string;
  requesterId: string | null;
}

interface Props {
  onClose: () => void;
  workerId: string;
  workerName: string;
  workerPhone: string;
  projectId: string;
  projectName: string;
  vacancyId?: string;
}

export function DeployModal({ onClose, workerId, workerName, workerPhone, projectId, projectName, vacancyId }: Props) {
  const qc = useQueryClient();
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [startTime, setStartTime] = useState('07:30');
  const [duration, setDuration] = useState('1 day');
  const { values: durations } = useReferenceList('durations', DEPLOY_DURATIONS);
  const durationOptions = durations.includes(duration) ? durations : [duration, ...durations];
  const [supervisorId, setSupervisorId] = useState('');
  const [meet, setMeet] = useState('');
  const [requesterId, setRequesterId] = useState('');
  const [notes, setNotes] = useState('');
  const [supervisorAutoFilled, setSupervisorAutoFilled] = useState(false);
  const [vacancyAutoFilled, setVacancyAutoFilled] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: contacts = [] } = useQuery({ queryKey: ['contacts'], queryFn: () => api.get<Contact[]>('/contacts').then((r) => r.data) });
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<ProjectMeta>(`/projects/${projectId}`).then((r) => r.data),
    enabled: !!projectId,
  });
  const { data: vacancy } = useQuery({
    queryKey: ['vacancy', vacancyId],
    queryFn: () => api.get<VacancyMeta>(`/vacancies/${vacancyId}`).then((r) => r.data),
    enabled: !!vacancyId,
  });
  const supervisor = contacts.find((c) => c.id === supervisorId);

  useEffect(() => {
    if (supervisorAutoFilled || !project) return;
    const def = project.supervisorId || project.clientPmId || project.clsManagerId || '';
    if (def) setSupervisorId(def);
    setSupervisorAutoFilled(true);
  }, [project, supervisorAutoFilled]);

  useEffect(() => {
    if (vacancyAutoFilled || !vacancy) return;
    if (vacancy.startDate) setStartDate(vacancy.startDate.slice(0, 10));
    if (vacancy.duration) setDuration(vacancy.duration);
    if (vacancy.requesterId) setRequesterId(vacancy.requesterId);
    setVacancyAutoFilled(true);
  }, [vacancy, vacancyAutoFilled]);

  const preview = useMemo(() => {
    const first = workerName.split(' ')[0] ?? workerName;
    const code = project?.code ?? projectId;
    const name = project?.name ?? projectName;
    const address = project?.address ?? '—';
    const postcode = project?.postcode ?? '—';
    const supLine = supervisor ? `${supervisor.name}${supervisor.phone ? ` · ${supervisor.phone}` : ''}` : '(not set)';
    const meetLine = meet.trim() || 'Report to site office';
    const startLine = `${startDate || 'TBC'} at ${startTime}`;
    const lines = [
      `Hi ${first},`,
      ``,
      `This is CLS — you've been assigned to:`,
      ``,
      `Project: ${code} — ${name}`,
      `Address: ${address}`,
      `Postcode: ${postcode}`,
      ``,
      `Start: ${startLine}`,
      `Duration: ${duration}`,
      ``,
      `Supervisor: ${supLine}`,
      `Meet: ${meetLine}`,
    ];
    if (notes.trim()) lines.push(``, `Notes: ${notes.trim()}`);
    lines.push(``, `Please confirm receipt of this message.`, ``, `— CLS Construction Logistics Services`);
    return lines.join('\n');
  }, [workerName, projectId, projectName, project, startDate, startTime, duration, supervisor, meet, notes]);

  const deploy = useMutation({
    mutationFn: async (sendWa: boolean) => {
      const body = {
        workerId,
        projectId,
        startDate,
        startTime,
        duration,
        supervisorId: supervisorId || null,
        meet: meet.trim() || null,
        requesterId: requesterId || null,
        notes: notes.trim() || null,
        vacancyId: vacancyId || null,
        waMessageSent: sendWa,
      };
      return api.post('/deployments', body);
    },
    onSuccess: (_data, sendWa) => {
      qc.invalidateQueries({ queryKey: ['deployments'] });
      qc.invalidateQueries({ queryKey: ['workers'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'overview'] });
      qc.invalidateQueries({ queryKey: ['proximity', workerId] });
      if (sendWa && workerPhone) {
        window.open(waLink(workerPhone, preview), '_blank');
      }
      toast.success('Worker deployed', `${workerName} → ${projectName}`);
      onClose();
    },
    onError: (e: any) => toast.error('Deploy failed', e?.response?.data?.message ?? 'Check fields and try again'),
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">🚀 Deploy Worker</div>
        <div className="modal-sub" id="deploy-mo-sub">{workerName} → {projectName}</div>
        <div className="fg2">
          <div className="form-row"><label className="form-label">Start Date</label><input type="date" className="form-input" id="dm-date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Start Time</label><input type="time" className="form-input" id="dm-time" value={startTime} onChange={(e) => setStartTime(e.target.value)} /></div>
        </div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Duration</label>
            <select className="form-select" id="dm-dur" value={duration} onChange={(e) => setDuration(e.target.value)}>
              {durationOptions.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">Supervisor / Contact</label>
            <SearchableSelect
              id="dm-sup"
              value={supervisorId}
              onChange={setSupervisorId}
              placeholder="— Select supervisor —"
              options={contacts.map((c) => ({ value: c.id, label: `${c.name}${c.role ? ` — ${c.role}` : ''}${c.customer ? ` · ${c.customer}` : ''}` }))}
            />
          </div>
        </div>
        <div className="form-row"><label className="form-label">Who to Meet on Arrival</label><input type="text" className="form-input" id="dm-meet" value={meet} onChange={(e) => setMeet(e.target.value)} /></div>
        <div className="form-row">
          <label className="form-label">Requested By <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>— client contact who asked for this worker</span></label>
          <SearchableSelect
            id="dm-requester"
            value={requesterId}
            onChange={setRequesterId}
            placeholder="— No requester —"
            options={contacts.map((c) => ({ value: c.id, label: `${c.name}${c.customer ? ` · ${c.customer}` : ''}${c.role ? ` — ${c.role}` : ''}` }))}
          />
        </div>
        <div className="form-row"><label className="form-label">Special Instructions</label><textarea className="form-textarea" id="dm-notes" placeholder="PPE, parking, site induction…" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        <div className="form-row">
          <label className="form-label">WhatsApp Message Preview</label>
          <div className="wa-preview" style={{ background: 'rgba(37,211,102,.08)', border: '1px solid rgba(37,211,102,.3)', borderRadius: 8, padding: 10 }}>
            <div className="wa-lbl" style={{ fontSize: 9, color: '#25D366', fontWeight: 700, marginBottom: 5, display: 'inline-flex', alignItems: 'center', gap: 4 }}><WaIcon size={11} /> Message preview</div>
            <div id="wa-preview" style={{ whiteSpace: 'pre-wrap', fontSize: 11 }}>{preview}</div>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-wa" style={{ background: '#25D366', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 5 }} onClick={() => deploy.mutate(true)} disabled={deploy.isPending || !startDate}><WaIcon size={13} /> WhatsApp &amp; Deploy</button>
          <button className="btn btn-primary" onClick={() => deploy.mutate(false)} disabled={deploy.isPending || !startDate}>
            {deploy.isPending ? 'Deploying…' : '✓ Deploy Only'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
